import { zodResolver } from "@hookform/resolvers/zod";
import { Info, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import { APIError, type WorksiteTimeSettings } from "@/adapters/api";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { useSaveTimeSettings } from "../../hooks/use-save-time-settings";
import { timeSettingsSchema } from "../../schemas/time-settings-schema";
import type { TimeSettingsFormValues } from "../../types";
import {
	timeSettingsErrorMessage,
	violationFieldPath,
	violationMessage,
} from "../../utils/errors";
import { toFormValues, toPutInput } from "../../utils/schedule";
import { OvertimeRatesForm } from "../overtime-rates-form";
import { ScheduleForm } from "../schedule-form";

export function TimeSettingsForm({
	settings,
	timezone,
}: {
	settings: WorksiteTimeSettings;
	timezone?: string;
}) {
	const save = useSaveTimeSettings();
	const [formError, setFormError] = useState<string | null>(null);

	const form = useForm<TimeSettingsFormValues>({
		resolver: zodResolver(timeSettingsSchema),
		defaultValues: toFormValues(settings),
	});
	const { handleSubmit, reset, setError } = form;

	useEffect(() => {
		reset(toFormValues(settings));
	}, [settings, reset]);

	async function onSubmit(values: TimeSettingsFormValues) {
		setFormError(null);
		try {
			await save.mutateAsync(toPutInput(values));
			toast.success("Jornada e horas extras salvas");
		} catch (err) {
			if (err instanceof APIError) {
				let matched = false;
				for (const [field, description] of Object.entries(err.violations)) {
					const path = violationFieldPath(field);
					if (!path) continue;
					matched = true;
					setError(path, { message: violationMessage(description) });
				}
				if (!matched) {
					setFormError(
						timeSettingsErrorMessage(
							err,
							"Não foi possível salvar as configurações.",
						),
					);
				}
				return;
			}
			setFormError(
				"Não foi possível salvar as configurações. Tente novamente.",
			);
		}
	}

	return (
		<form
			className="time-settings-form"
			onSubmit={handleSubmit(onSubmit)}
			noValidate
		>
			<Card>
				<CardHeader>
					<CardTitle>Jornada</CardTitle>
					<CardDescription>
						Horário previsto por dia da semana. As horas esperadas descontam o
						intervalo.
					</CardDescription>
				</CardHeader>
				<CardContent>
					<ScheduleForm form={form} />
					{timezone && (
						<p className="time-settings-form__note">
							<Info />
							Horários no fuso do canteiro ({timezone}).
						</p>
					)}
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle>Horas extras</CardTitle>
					<CardDescription>
						Acréscimo percentual aplicado sobre a hora normal em cada tipo de
						dia.
					</CardDescription>
				</CardHeader>
				<CardContent>
					<OvertimeRatesForm form={form} />
				</CardContent>
			</Card>

			{formError && (
				<div className="form-error-alert" role="alert">
					{formError}
				</div>
			)}

			<div className="time-settings-form__actions">
				<Button type="submit" disabled={save.isPending}>
					{save.isPending && (
						<Loader2 className="time-settings-form__spinner" />
					)}
					Salvar
				</Button>
			</div>
		</form>
	);
}
